import { useCallback, useState } from "react";
import { CheckCircle2, AlertTriangle, ShieldCheck } from "lucide-react";
import { useDolfinClient } from "@/components/DolfinProvider";
import { apiErrorMessage } from "@/lib/api-error";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Spinner } from "@/components/ui/spinner";

// The generated client types the 400 as plain ProblemDetails, but /validate returns a ValidationProblem; narrow it.
type ValidationProblem = {
  errors?: Record<string, string[]>;
  codes?: Array<{ field: string; code: string }>;
};

type Issue = { field: string; message: string; code?: string };

function toIssues(problem: ValidationProblem | undefined): Issue[] {
  const errors = problem?.errors;
  if (!errors || typeof errors !== "object") return [];
  const codeByField = new Map((problem?.codes ?? []).map((c) => [c.field, c.code] as const));
  return Object.entries(errors).flatMap(([field, messages]) =>
    (messages ?? []).map((message) => ({ field, message, code: codeByField.get(field) })),
  );
}

type ValidationStatus =
  | { kind: "valid" }
  | { kind: "invalid"; issues: Issue[] }
  | { kind: "error"; message: string };

export function BillValidationCard({ billId }: { billId: string }) {
  const api = useDolfinClient();
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [status, setStatus] = useState<ValidationStatus | null>(null);

  const validate = useCallback(async () => {
    setLoading(true);
    try {
      const { data, error, response } = await api.GET("/v1/bills/{id}/validate", {
        params: { path: { id: billId } },
      });

      if (data?.valid) {
        setStatus({ kind: "valid" });
        return;
      }

      // 400 lists the bill's issues; anything else is a request failure, not a validation result.
      if (response?.status === 400) {
        const issues = toIssues(error as ValidationProblem | undefined);
        setStatus(
          issues.length > 0
            ? { kind: "invalid", issues }
            : { kind: "error", message: apiErrorMessage(error, "Validation failed.") },
        );
        return;
      }

      setStatus({ kind: "error", message: apiErrorMessage(error, "Couldn't validate this bill.") });
    } catch (e) {
      setStatus({ kind: "error", message: "Couldn't validate this bill." });
    } finally {
      setLoading(false);
    }
  }, [api, billId]);

  const onOpenChange = useCallback(
    (next: boolean) => {
      setOpen(next);
      if (next) {
        setStatus(null);
        void validate();
      }
    },
    [validate],
  );

  return (
    <>
      <Button variant="outline" className="w-full justify-center gap-2" onClick={() => onOpenChange(true)}>
        <ShieldCheck className="size-4" />
        Run Validation
      </Button>

      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Bill validation</DialogTitle>
            <DialogDescription>
              Checks that line items reconcile to the subtotal, subtotal plus tax equals the total,
              and the required details are present. Issues are warnings and do not block submitting
              the bill for review.
            </DialogDescription>
          </DialogHeader>

          {loading ? (
            <div className="flex justify-center py-8">
              <Spinner className="size-5 text-muted-foreground" />
            </div>
          ) : status?.kind === "valid" ? (
            <div className="flex items-start gap-2 rounded-lg border border-emerald-500/30 bg-emerald-500/5 p-3">
              <CheckCircle2 className="mt-0.5 size-4 shrink-0 text-emerald-600" />
              <p className="text-sm font-medium text-emerald-700 dark:text-emerald-400">
                Bill successfully validated
              </p>
            </div>
          ) : status?.kind === "invalid" ? (
            <div className="space-y-2">
              <div className="flex items-center gap-2">
                <AlertTriangle className="size-4 shrink-0 text-amber-600" />
                <p className="text-sm font-medium text-amber-600">
                  {status.issues.length} validation{" "}
                  {status.issues.length === 1 ? "issue" : "issues"} found
                </p>
              </div>
              <ul className="space-y-2">
                {status.issues.map((issue, i) => (
                  <li
                    key={`${issue.field}-${issue.code ?? ""}-${i}`}
                    className="flex items-start gap-2 rounded-lg border border-amber-500/30 bg-amber-500/5 p-2.5"
                  >
                    <AlertTriangle className="mt-0.5 size-4 shrink-0 text-amber-600" />
                    <p className="text-sm text-foreground">{issue.message}</p>
                  </li>
                ))}
              </ul>
            </div>
          ) : status?.kind === "error" ? (
            <p className="py-2 text-sm text-muted-foreground">{status.message}</p>
          ) : null}
        </DialogContent>
      </Dialog>
    </>
  );
}
